import { type IconName, isIconName } from '@iconik-io/uad';

import { type IconType, isIconType } from './constants';

const legacyToUadIconMap: Record<IconType, IconName | null> = {
  AddToCollection: 'collection_add_to',
  Ai: 'ai',
  AiMetadata: 'ai_metadata',
  Analyze: 'analyze',
  Annotation: 'annotation_annotation',
  Archive: 'archive_archive',
  ArrowLeft: 'arrow_small_w',
  ArrowSmallDown: 'chevron_small_isolated_s',
  ArrowSmallRight: 'chevron_small_isolated_e',
  AssetTrashBin: 'asset_delete',
  Audio: 'asset_type_audio',
  Automations: 'automations',
  Back: 'revert',
  Bell: 'notification',
  BellCrossed: 'bell_crossed',
  Billing: 'money_billing',
  Calendar: 'calendar',
  Camera: 'snapshot',
  Card: 'money_credit_card',
  Clapper: 'project',
  Clear: 'broom',
  Clock: 'pending',
  ClosedCaption: 'subtitles',
  Cloud: 'storage_cloud',
  // Cog can map to menu_context or settings depending on context
  Cog: 'menu_context',
  Collection: 'collection',
  CollectionIsg: 'isg',
  Comment: 'comment',
  Copy: 'copy',
  CornerDown: 'chevron_small_isolated_s',
  CrossMark: 'close',
  CrossMarkSmallFilled: 'deletion_clear',
  CustomAction: 'custom_action',
  Datetime: 'calendar_datetime',
  Divide: 'divide',
  Download: 'load_down',
  DrawArrow: 'annotation_arrow',
  DrawCircle: 'annotation_circle',
  DrawGenerate: 'annotation_brush',
  DrawLine: 'annotation_line',
  DrawPalette: 'annotation_color_palette',
  DrawRectangle: 'annotation_rectangle',
  DrawRedo: 'annotation_redo',
  DrawText: 'annotation_text',
  DrawUndo: 'annotation_undo',
  Dropdown: 'metadata_dropdown',
  Edit: 'edit',
  EditComment: 'edit',
  Enter: 'enter',
  Erase: 'annotation_erase',
  Error: 'message_error',
  Expand: 'fit_viewport',
  Export: 'file_export',
  Eye: 'display_show',
  FaceRecognition: 'face_recognition',
  Favourite: 'favorites_add_to',
  FavouriteFilled: 'favorites_added_to',
  File: 'file',
  Filter: 'filter',
  Fullscreen: 'player_view_full',
  Globe: 'globe',
  GoToPage: 'open',
  Grid: 'view_grid',
  Group: 'user_group',
  Hamburger: 'menu_hamburger',
  Help: 'help_tooltip',
  History: 'history',
  Id: 'id',
  Image: 'asset_type_image',
  InPoint: 'range_in_point',
  Info: 'help_tooltip',
  Integer: 'metadata_integer',
  Invoice: 'money_invoice',
  Job: 'jobs',
  Key: 'key',
  Link: 'link',
  LinkAdd: 'link_add',
  // List can map to view_list or text_list_bullet depending on context
  List: 'view_list',
  Lock: 'padlock_locked',
  Logout: 'log_out',
  Mail: 'email',
  MailError: 'email_error',
  MenuVertical: 'menu_context',
  Metadata: 'Svg2022_uad_metadata',
  Minimize: 'player_view_normal',
  Minus: 'minus',
  MobilePhone: 'smartphone',
  Move: 'move',
  NavDown: 'chevron_large_isolated_s',
  NavLeft: 'chevron_large_isolated_w',
  NavRight: 'chevron_large_isolated_e',
  NavUp: 'chevron_large_isolated_n',
  NotModified: 'not_modified',
  Open: 'open',
  OutPoint: 'range_out_point',
  Pause: 'player_pause',
  PdfDoc: 'asset_type_pdf',
  Permission: 'access_control',
  PersonChange: 'fr_change_person',
  Pin: 'pin',
  Placeholder: 'asset_type_placeholder',
  Play: 'player_play',
  PlayFilled: 'player_play',
  Playlist: 'playlist',
  Plus: 'plus',
  Project: 'project',
  Proxy: 'file_proxy',
  Publish: 'send',
  Range: 'range_range',
  Refresh: 'refresh',
  Relation: 'relations',
  RemoveContent: 'deletion_erase',
  Reorder: 'reorder',
  Repeat: 'player_loop',
  Reply: 'reply',
  Request: 'ra_approval_requested',
  RequestedFilled: 'ra',
  Reset: 'reset',
  Restore: 'archive_restore',
  RoleGroups: 'role_groups',
  SavedSearch: 'saved_search',
  Search: 'search',
  SelectAll: 'selection_items_all',
  Selection: 'selection_items',
  Sequence: 'sequence',
  Share: 'share',
  Show: 'display_show',
  SignCrossed: 'message_error',
  Sort: 'sort',
  SortAsc: 'arrow_small_n',
  SortBy: 'sort',
  SortDesc: 'arrow_small_s',
  SoundOff: 'player_volume_no',
  SoundOn: 'player_volume_max',
  Stat: 'statistics',
  Storage: 'storage_on_premise',
  // TODO: UAD icons, talk to UX for potential stream icon
  Stream: 'asset_type_video',
  String: 'metadata_text',
  Subclip: 'asset_type_subclip',
  Subtitle: 'subtitles',
  Switch: 'toggle_on',
  Tag: 'tag',
  Text: 'metadata_text_area',
  ThumbDown: 'ra_rejected',
  ThumbDownFilled: 'ra_rejected_by_me',
  ThumbMixed: 'ra',
  ThumbMixedFilled: 'ra',
  ThumbUp: 'ra_approved',
  ThumbUpFilled: 'ra_approved_by_me',
  Tick: 'checkmark',
  TickChecked: 'loader_circle_done_legacy',
  TickCheckedFilled: 'loader_circle_done_legacy',
  TickUnchecked: 'annotation_circle',
  TimeBasedMetadata: 'Svg2022_uad_time_based_metadata',
  Transcoder: 'transcoder',
  Transcription: 'transcription',
  Transfer: 'transfer',
  TrashBin: 'deletion_delete',
  Unlock: 'padlock_unlocked',
  Upload: 'load_up',
  UserAdd: 'user_add',
  User: 'user_user',
  Version: 'versions',
  VersionAdd: 'versions_add_new',
  Video: 'asset_type_video',
  Warning: 'message_warning',
  Webhook: 'webhook',

  // TODO: UAD icons, await icon approval
  // Custom: 'job_custom'
  Custom: null,
  // TODO: UAD icons, await icon approval
  // FileSetTrashBin: 'file_set_delete',
  FileSetTrashBin: null,
  // TODO: UAD icons, this icon will remain the same, but needs to be added to the Figma file "Used in implementation"
  // https://www.figma.com/design/7tRLvGETlA47pV5lpaC116/UAD-Icons
  Iconik: null,
  // TODO: UAD icons, ask UX to rename index in the figma file
  // Index: 'index',
  Index: null,
  // TODO: UAD icons, await icon approval
  // UploadHistory: 'upload_history',
  UploadHistory: null,
  // TODO: UAD icons, talk to UX for a potential wave icon
  Wave: null,

  // Icons not used in Iconik Web
  AltKey: null,
  ArrowRight: null,
  Column: null,
  CrossMarkFilled: null,
  Discovery: null,
  Dnd: null,
  DrawPaletteColor: null,
  DrawPencil: null,
  DrawSelectMove: null,
  ExpandHeight: null,
  ExpandWidth: null,
  FastForward: null,
  Float: null,
  GroupAdd: null,
  LinkCopy: null,
  MailType: null,
  PauseFilled: null,
  Progress: null,
  Reconnect: null,
  Reverse: null,
  Signature: null,
  SplitView: null,
  UnselectAll: null,
};

export function mapLegacyIconName(
  icon: string | null | undefined,
): IconName | null | undefined {
  if (!icon) {
    return null;
  }

  if (isIconName(icon)) {
    return icon;
  }

  if (!isIconType(icon)) {
    return undefined;
  }
  return legacyToUadIconMap[icon];
}
